// src/services/costs/estimateExcelExport.js
// Excel export for Kostenschätzungs-Dossier (matches Vorlage_FP_Kostenschaetzung.xlsx layout)
import ExcelJS from "exceljs";

const UNIT_LABELS = {
  pl: "pauschal",
  st: "Stück",
  m: "Laufmeter",
  m2: "m²",
  m3: "m³",
  h: "Stunden",
  t: "Tonne",
  kg: "Kilogramm",
};

function getAccuracyFromPhase(phase) {
  if (!phase) return 25;
  const phaseNum = parseInt(phase.substring(0, 2));
  if (phaseNum === 21) return 25;
  if (phaseNum === 31) return 15;
  return 10;
}

/** Convert 1-based column number to Excel letter (1→A, 2→B, 27→AA) */
function colLetter(n) {
  let s = "";
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s;
}

/**
 * Collect visible nodes in tree-order (depth-first), respecting filters and showInactive.
 * Mirrors the visibility logic in EstimateEditor.renderTree.
 */
function collectVisibleNodes(tree, filteredCodes, showInactive) {
  const nodes = [];

  const hasActiveDescendant = (n) => {
    if (!n) return false;
    if (n.active) return true;
    for (const c of n.children || []) {
      const child = tree[c];
      if (child && hasActiveDescendant(child)) return true;
    }
    return false;
  };

  const walk = (node) => {
    if (!node) return;
    if (filteredCodes && !filteredCodes.has(node.code)) return;

    if (!showInactive && !node.active && node.level !== 1) {
      const hasContent = node.children?.length > 0 && node.totalComputed > 0;
      if (!hasContent && !hasActiveDescendant(node)) return;
    }

    nodes.push(node);

    if (node.children) {
      for (const childCode of node.children) {
        if (tree[childCode]) walk(tree[childCode]);
      }
    }
  };

  Object.values(tree)
    .filter((n) => n.level === 1)
    .sort((a, b) => parseInt(a.code) - parseInt(b.code))
    .forEach((n) => walk(n));

  return nodes;
}

function buildTotalFormula(row, c) {
  const L = colLetter(c.length);
  const W = colLetter(c.width);
  const H = colLetter(c.height);
  const Q = colLetter(c.qty);
  const U = colLetter(c.unit);
  const P = colLetter(c.price);
  const F = colLetter(c.factor);
  const r = row;

  return (
    `ROUND(IF(${U}${r}="pauschal",${P}${r}*${F}${r},` +
    `IF(${L}${r}>0,${L}${r},1)*IF(${W}${r}>0,${W}${r},1)*` +
    `IF(${H}${r}>0,${H}${r},1)*IF(${Q}${r}>0,${Q}${r},1)*${P}${r}*${F}${r}),2)`
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const TITLE_FONT = { bold: true, size: 14 };
const LABEL_FONT = { bold: true, size: 10 };
const HEADER_FONT = { bold: true, size: 9 };
const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
const HEADER_BORDER = { bottom: { style: "medium", color: { argb: "FF333333" } } };
const LEVEL1_FONT = { bold: true, size: 10 };
const LEVEL1_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F7F7" } };
const LEVEL2_FONT = { bold: true, size: 9 };
const NORMAL_FONT = { size: 9 };
const CHF_FMT = `"CHF "#,##0.00`;
const NUM_FMT = `#,##0.00`;

// ── Main export ─────────────────────────────────────────────────────────────

export async function exportEstimateToExcel({
  tree,
  filteredCodes,
  showInactive,
  estimateName,
  linkedProject,
  currentPhase,
  visibleCustomColumns = [],
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Malheur&Fortuna GmbH";
  workbook.lastModifiedBy = "Malheur&Fortuna GmbH";
  workbook.created = new Date();
  workbook.modified = new Date();

  // outlineLevelRow muss >= max. outlineLevel aller Zeilen sein, sonst
  // serialisiert ExcelJS jede gruppierte Zeile als collapsed="1"
  // (row.js get collapsed()), was Excel als korrupt meldet.
  // outlineProperties (summaryBelow) wird bewusst NICHT gesetzt: ExcelJS 4.4.0
  // serialisiert die sheetPr-Children in falscher Reihenfolge
  // (pageSetUpPr vor outlinePr) — das verletzt CT_SheetPr im OOXML-Schema
  // (Reihenfolge muss tabColor→outlinePr→pageSetUpPr sein) und macht die
  // Datei in Excel korrupt. Ohne outlineProperties bleibt sheetPr schlank.
  const ws = workbook.addWorksheet("Kostenschätzung", {
    properties: { outlineLevelRow: 5 },
  });

  // ── Dynamic column indices (1-based) ────────────────────────────────────
  const customCols = visibleCustomColumns || [];
  const COL = {
    bkp: 2,
    name: 3,
    source: 4,
    tags: 5,
  };
  const CUSTOM_START = 6;
  COL.length = CUSTOM_START + customCols.length;
  COL.width = COL.length + 1;
  COL.height = COL.width + 1;
  COL.qty = COL.height + 1;
  COL.unit = COL.qty + 1;
  COL.price = COL.unit + 1;
  COL.factor = COL.price + 1;
  COL.total = COL.factor + 1;

  const totalLetter = colLetter(COL.total);

  // ── Column widths ───────────────────────────────────────────────────────
  ws.getColumn(1).width = 16;
  ws.getColumn(COL.bkp).width = 10;
  ws.getColumn(COL.name).width = 40;
  ws.getColumn(COL.source).width = 14;
  ws.getColumn(COL.tags).width = 18;
  customCols.forEach((_, i) => {
    ws.getColumn(CUSTOM_START + i).width = 15;
  });
  ws.getColumn(COL.length).width = 10;
  ws.getColumn(COL.width).width = 10;
  ws.getColumn(COL.height).width = 10;
  ws.getColumn(COL.qty).width = 10;
  ws.getColumn(COL.unit).width = 12;
  ws.getColumn(COL.price).width = 15;
  ws.getColumn(COL.factor).width = 10;
  ws.getColumn(COL.total).width = 18;

  // ── Metadata (rows 1-9) ────────────────────────────────────────────────
  const accuracy = getAccuracyFromPhase(currentPhase);
  const projectLabel = linkedProject
    ? `${linkedProject.code} - ${linkedProject.name}${estimateName ? ` – ${estimateName}` : ""}`
    : estimateName || "";

  ws.getCell("A1").value = "Kostentabelle Nach BKP und Gewerk";
  ws.getCell("A1").font = TITLE_FONT;
  ws.mergeCells(1, 1, 1, COL.total);

  ws.getCell("A2").value = "Projekt";
  ws.getCell("A2").font = LABEL_FONT;
  ws.getCell("B2").value = projectLabel;

  ws.getCell("A3").value = "Phase";
  ws.getCell("A3").font = LABEL_FONT;
  ws.getCell("B3").value = currentPhase || "";

  ws.getCell("A4").value = "Kostengenauigkeit";
  ws.getCell("A4").font = LABEL_FONT;
  ws.getCell("B4").value = `±${accuracy}%`;

  ws.getCell("A5").value = "Datum";
  ws.getCell("A5").font = LABEL_FONT;
  ws.getCell("B5").value = new Date().toLocaleDateString("de-CH");

  ws.getCell("A8").value = "Verfasserin";
  ws.getCell("A8").font = LABEL_FONT;
  ws.getCell("B8").value = "Malheur&Fortuna GmbH";
  ws.getCell("B9").value = "Claragraben 103, 4057 Basel";

  // ── Header row (11) ────────────────────────────────────────────────────
  const HEADER_ROW = 11;
  const headerDefs = [
    [COL.bkp, "BKP-Nr"],
    [COL.name, "Name"],
    [COL.source, "Source"],
    [COL.tags, "Tags"],
    ...customCols.map((col, i) => [CUSTOM_START + i, col.name]),
    [COL.length, "Länge"],
    [COL.width, "Breite"],
    [COL.height, "Höhe"],
    [COL.qty, "Anzahl"],
    [COL.unit, "Einheit"],
    [COL.price, "EP"],
    [COL.factor, "Faktor"],
    [COL.total, "Total (CHF)"],
  ];

  for (const [col, label] of headerDefs) {
    const cell = ws.getCell(HEADER_ROW, col);
    cell.value = label;
    cell.font = HEADER_FONT;
    cell.fill = HEADER_FILL;
    cell.border = HEADER_BORDER;
  }

  // Freeze header rows so they stay visible during scrolling
  ws.views = [{ state: "frozen", ySplit: HEADER_ROW }];

  // ── Data rows ──────────────────────────────────────────────────────────
  const DATA_START = 12;
  const visibleNodes = collectVisibleNodes(tree, filteredCodes, showInactive);

  // Map each node code → its Excel row number.
  // Kommentare belegen eine eigene Folgezeile, deshalb laufende Nummerierung
  // statt DATA_START + Index (sonst verschieben sich alle Formel-Referenzen).
  const codeToRow = {};
  let nextRow = DATA_START;
  visibleNodes.forEach((node) => {
    codeToRow[node.code] = nextRow;
    nextRow += node.comment ? 2 : 1;
  });
  const dataRowCount = nextRow - DATA_START;

  visibleNodes.forEach((node) => {
    const rowNum = codeToRow[node.code];
    const isActive = node.active;
    const hasVisibleChildren = (node.children || []).some(
      (c) => codeToRow[c] !== undefined
    );
    const isAggregatingParent = !isActive && hasVisibleChildren;

    // BKP-Hierarchie als Excel-Outline: Level 1 = 0 (top), Level 2 = 1, …, Level 5 = 4
    ws.getRow(rowNum).outlineLevel = Math.max(0, (node.level || 1) - 1);

    const font =
      node.level === 1
        ? LEVEL1_FONT
        : node.level === 2
          ? LEVEL2_FONT
          : NORMAL_FONT;

    // Helper: apply level-1 background to a cell
    const applyL1 = (cell) => {
      if (node.level === 1) cell.fill = LEVEL1_FILL;
    };

    // ── Text columns ──────────────────────────────────────────────────
    const bkpCell = ws.getCell(rowNum, COL.bkp);
    bkpCell.value = node.code;
    bkpCell.font = font;
    applyL1(bkpCell);

    const nameCell = ws.getCell(rowNum, COL.name);
    nameCell.value = node.name || "";
    nameCell.font = font;
    applyL1(nameCell);

    const srcCell = ws.getCell(rowNum, COL.source);
    srcCell.value = node.source || "";
    srcCell.font = font;
    applyL1(srcCell);

    const tagCell = ws.getCell(rowNum, COL.tags);
    tagCell.value = (node.tags || []).join(", ");
    tagCell.font = font;
    applyL1(tagCell);

    // Custom columns
    customCols.forEach((col, ci) => {
      const c = ws.getCell(rowNum, CUSTOM_START + ci);
      const vals = node.customFields?.[col.id];
      c.value = Array.isArray(vals) ? vals.join(", ") : "";
      c.font = font;
      applyL1(c);
    });

    // ── Calculation columns + Total ───────────────────────────────────
    if (isAggregatingParent) {
      // Inactive parent: SUM of direct children that are in the export
      const childRowNums = (node.children || [])
        .filter((c) => codeToRow[c] !== undefined)
        .map((c) => codeToRow[c]);

      // Fill empty calc cells with level-1 bg if needed
      for (let c = COL.length; c < COL.total; c++) {
        applyL1(ws.getCell(rowNum, c));
      }

      const totalCell = ws.getCell(rowNum, COL.total);
      if (childRowNums.length > 0) {
        const refs = childRowNums.map((r) => `${totalLetter}${r}`).join(",");
        totalCell.value = { formula: `SUM(${refs})`, result: node.totalComputed };
      } else {
        totalCell.value = node.totalComputed;
      }
      totalCell.numFmt = CHF_FMT;
      totalCell.font = font;
      applyL1(totalCell);
    } else if (isActive) {
      // Active node: write values + total formula
      const setNum = (col, val) => {
        const c = ws.getCell(rowNum, col);
        c.value = val;
        c.numFmt = NUM_FMT;
        c.font = font;
      };

      setNum(COL.length, node.length || 0);
      setNum(COL.width, node.width || 0);
      setNum(COL.height, node.height || 0);
      setNum(COL.qty, node.quantity || 0);

      const unitCell = ws.getCell(rowNum, COL.unit);
      unitCell.value = UNIT_LABELS[node.unit] || node.unit || "";
      unitCell.font = font;

      const priceCell = ws.getCell(rowNum, COL.price);
      priceCell.value = node.unitPrice || 0;
      priceCell.numFmt = CHF_FMT;
      priceCell.font = font;

      setNum(COL.factor, node.factor || 1);

      const totalCell = ws.getCell(rowNum, COL.total);
      totalCell.value = {
        formula: buildTotalFormula(rowNum, COL),
        result: node.totalComputed,
      };
      totalCell.numFmt = CHF_FMT;
      totalCell.font = font;
    } else {
      // Inactive leaf (no visible children): 0 so parent SUMs stay correct
      const totalCell = ws.getCell(rowNum, COL.total);
      totalCell.value = 0;
      totalCell.numFmt = CHF_FMT;
      totalCell.font = { ...font, color: { argb: "FF999999" } };
    }

    // ── Kommentarzeile: kursiv, nur in der Name-Spalte, sonst leer ──────
    if (node.comment) {
      const commentRowNum = rowNum + 1;
      ws.getRow(commentRowNum).outlineLevel = Math.max(0, (node.level || 1) - 1);
      const commentCell = ws.getCell(commentRowNum, COL.name);
      commentCell.value = node.comment;
      commentCell.font = { size: 9, italic: true };
      commentCell.alignment = { wrapText: true, vertical: "top" };
    }
  });

  // ── Grand Total row ────────────────────────────────────────────────────
  const TOTAL_ROW = DATA_START + dataRowCount + 1;
  const level1Rows = visibleNodes
    .filter((n) => n.level === 1)
    .map((n) => codeToRow[n.code]);

  const totalLabelCell = ws.getCell(TOTAL_ROW, COL.factor);
  totalLabelCell.value = "Total";
  totalLabelCell.font = { bold: true, size: 11 };
  totalLabelCell.alignment = { horizontal: "right" };

  if (level1Rows.length > 0) {
    const refs = level1Rows.map((r) => `${totalLetter}${r}`).join(",");
    const grandTotal = level1Rows.reduce((s, r) => {
      const node = visibleNodes.find((n) => codeToRow[n.code] === r);
      return s + (node?.totalComputed || 0);
    }, 0);

    const grandTotalCell = ws.getCell(TOTAL_ROW, COL.total);
    grandTotalCell.value = { formula: `SUM(${refs})`, result: grandTotal };
    grandTotalCell.numFmt = CHF_FMT;
    grandTotalCell.font = { bold: true, size: 11 };
    grandTotalCell.border = {
      top: { style: "medium", color: { argb: "FF333333" } },
    };
  }

  // ── Page setup ─────────────────────────────────────────────────────────
  ws.pageSetup.orientation = "landscape";
  ws.pageSetup.fitToPage = true;
  ws.pageSetup.fitToWidth = 1;
  ws.pageSetup.fitToHeight = 0;

  // ── Download ───────────────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;

  const now = new Date();
  const dateStr =
    String(now.getFullYear()).slice(2) +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const projectCode = linkedProject?.code || "";
  const phasePrefix = currentPhase ? currentPhase.split(" ")[0] : "";
  const safeName = (estimateName || "Kostenschaetzung").replace(
    /[^a-zA-Z0-9äöüÄÖÜ_-]/g,
    "_"
  );
  a.download = `${projectCode ? projectCode + "_" : ""}KS_${phasePrefix}_${dateStr}_${safeName}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}
